import { useMutation } from '@provision/graphql-client';
import dayjs from 'dayjs';
import { useCallback, useEffect } from 'react';
import { isNullish } from 'remeda';
import { UserPresenceStatus } from 'src/graphql/entities/user-presence/user-presence';
import { UPDATE_USER_PRESENCE_MUTATION } from 'src/graphql/entities/user-presence/user-presence.operations';
import { selectUser, useUserStore } from 'src/store/current-user';
import { useBroadcastChannelLeaderTab } from 'src/utils/hooks';

const INTERVAL_MS = 60_000;

export function useTrackUserPresence() {
  const user = useUserStore(selectUser);
  const [mutate] = useMutation(UPDATE_USER_PRESENCE_MUTATION);
  const enabled = !isNullish(user?.partnerId);
  const leaderScope = enabled ? `presence:${user.userId ?? user.id}` : '';
  const isLeaderTab = useBroadcastChannelLeaderTab(leaderScope, enabled);

  const send = useCallback(
    (partnerId: number, status: UserPresenceStatus) => {
      if (!isLeaderTab) return Promise.resolve();
      const groups =
        user?.conversationAssigneeGroups?.map(({ id, name }) => ({ id, name })) ?? [];

      const timezone = dayjs.tz?.guess?.() ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

      return mutate({
        variables: {
          params: {
            status,
            partnerId,
            tz: timezone,
            assigneeGroups: groups,
          },
        },
      }).catch(console.error);
    },
    [isLeaderTab, mutate, user?.conversationAssigneeGroups],
  );

  useEffect(() => {
    if (isNullish(user?.partnerId)) return;

    const pid = user.partnerId;
    let t: number | null = null;
    let stopped = false;

    const clear = () => {
      if (t !== null) window.clearTimeout(t);
      t = null;
    };

    const schedule = () => {
      if (stopped) return;
      const jitter = INTERVAL_MS * (Math.random() * 0.3 - 0.15); // ±15%
      t = window.setTimeout(
        async () => {
          await send(pid, UserPresenceStatus.active);
          schedule();
        },
        Math.max(10_000, INTERVAL_MS + jitter),
      );
    };

    const start = () => {
      clear();
      stopped = false;
      void send(pid, UserPresenceStatus.active);
      schedule();
    };

    const goInactive = () => void send(pid, UserPresenceStatus.inactive);

    // initial
    if (
      document.visibilityState === 'visible' &&
      (typeof navigator === 'undefined' || navigator.onLine)
    ) {
      start();
    }

    // lifecycle
    const onVis = () =>
      document.visibilityState === 'visible'
        ? start()
        : (goInactive(), clear(), (stopped = true));
    const onOnline = () => document.visibilityState === 'visible' && start();
    const onOffline = () => (clear(), (stopped = true));
    const onHide = () => (goInactive(), clear(), (stopped = true));

    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    window.addEventListener('pagehide', onHide);

    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('pagehide', onHide);
      onHide();
    };
  }, [user?.partnerId, send]);
}
